import { TemplateWrapper } from './Wrapper';
import { Controller } from 'react-hook-form';
import { Textarea } from '@design-system/components/textarea/textarea';
import { MenuItem, Select } from '@mui/material';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';
import { InputNumber } from '@design-system/components/input/inputNumber';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { bgColorsVars } from '@design-system/vars.css';
import { EditWorkoutVM } from '../../view-models/workoutEdit.viewModel';
import { WorkoutItemTemplates } from '../../view-models/mappers';
import { ArrayItem, TypedOmit } from '@client/new/helpers/types';
import {
  LegacyExercise as ExerciseType,
  Set,
} from '../../models/workout.domain';
import {
  SetDoseCard,
  SetDoseErrors,
} from '@client/new/components/SetDoseCard/SetDoseCard';
import { InfoAlert } from '@client/new/components/InfoAlert';
import { TitleInputWithHints } from '../../components/titleInputWithHints/TitleInputWithHints';
import { Hint, HintType } from '../../components/Hint';
import { MediaPreview } from '../../components/MediaPreview';
import { clsx } from 'clsx';
import { CreatorApprovedSwapsField } from '../CreatorApprovedSwapsField';

type ExerciseProps = {
  index: number;
  circuitIndex?: number;
  expand: boolean;
} & Pick<
  EditWorkoutVM,
  | 'influencerId'
  | 'dispatch'
  | 'control'
  | 'submitting'
  | 'inputHints'
  | 'readonly'
  | 'exerciseSuggestions'
> &
  Pick<ArrayItem<WorkoutItemTemplates>, 'header' | 'menuItems'> &
  TypedOmit<ExerciseType, 'id'> & { id?: string };

const ExerciseMedia = (p: ExerciseProps) => {
  return (
    <div>
      <MediaPreview media={p.media ?? undefined} videoType="short" />
    </div>
  );
};

export const Exercise = (p: ExerciseProps) => {
  const showRestBetween = (p.sets || []).length > 1;
  return (
    <div>
      <TemplateWrapper
        {...p}
        isCircuit={p.circuitIndex !== undefined && p.circuitIndex >= 0}
      >
        <div className="bg-white w-full flex flex-col p-4 gap-6 rounded-b-xl shadow-exercise">
          <div className="w-full flex flex-col md:flex-row gap-6">
            <div className="w-[244px]">
              {p.media ? (
                <MediaPreview
                  media={p.media}
                  videoType="short"
                  removeVideo={() =>
                    p.dispatch('removeMedia', {
                      index: p.index,
                      circuitIndex: p.circuitIndex,
                    })
                  }
                  disabled={p.readonly}
                />
              ) : (
                <div className="w-[244px] h-[220px]">
                  <Button
                    color="neutral"
                    onClick={() =>
                      p.dispatch('openLibrary', {
                        index: p.index,
                        circuitIndex: p.circuitIndex,
                        type: 'short',
                        action: 'update',
                      })
                    }
                    leftIcon={faPlus}
                    disabled={p.readonly}
                  >
                    Add
                  </Button>
                </div>
              )}
            </div>
            <div className="w-full flex flex-col gap-2">
              <Controller
                name={
                  p?.circuitIndex !== undefined
                    ? `exercises.${p.circuitIndex}.exercises.${p.index}.name`
                    : `exercises.${p.index}.name`
                }
                control={p.control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <TitleInputWithHints
                    required={true}
                    value={value}
                    onChange={onChange}
                    onSearchChange={(nextValue) =>
                      p.dispatch('setExerciseSearch', nextValue)
                    }
                    onBlur={() => {
                      p.dispatch('clearRecommendations');
                    }}
                    disabled={p.submitting || p.readonly}
                    error={fieldState.error?.message}
                    exerciseSuggestions={p.exerciseSuggestions}
                    onUpdate={(description: string, uuid: string) =>
                      p.dispatch('exerciseTitleUpdate', {
                        circuitIndex: p.circuitIndex,
                        index: p.index,
                        description,
                        uuid,
                      })
                    }
                  />
                )}
              />
              <Controller
                name={
                  p?.circuitIndex !== undefined
                    ? `exercises.${p.circuitIndex}.exercises.${p.index}.description`
                    : `exercises.${p.index}.description`
                }
                control={p.control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Textarea
                    label="Description"
                    rows={4}
                    disabled={p.submitting || p.readonly}
                    value={value}
                    onChange={onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <InfoAlert text="Changes here won't update the title or description in the Library. Update the file in Library for changes." />
              <Hint
                hint={p.inputHints?.[p.uuid || '']}
                onUpdate={(hint: HintType) =>
                  p.dispatch('applyHint', {
                    circuitIndex: p.circuitIndex,
                    index: p.index,
                    hint,
                    uuid: p.uuid || '',
                  })
                }
              />
              <Controller
                name={
                  p?.circuitIndex !== undefined
                    ? `exercises.${p.circuitIndex}.exercises.${p.index}.unilateral`
                    : `exercises.${p.index}.unilateral`
                }
                control={p.control}
                render={({ field: { onChange, value } }) => (
                  <div
                    className="flex justify-between items-center pt-2"
                    data-testid="unilateral"
                  >
                    <Typography>Unilateral</Typography>
                    <Toggle
                      value={value ?? false}
                      variant="primary"
                      onChange={onChange}
                      disabled={p.submitting || p.readonly}
                    />
                  </div>
                )}
              />
            </div>
            <div className="w-full flex flex-col pt-[28px]">
              <Sets
                sets={p.sets || []}
                index={p.index}
                dispatch={p.dispatch}
                submitting={p.submitting}
                circuitIndex={p.circuitIndex}
                readonly={p.readonly}
                control={p.control}
              />
              {showRestBetween ? (
                <RestBetween
                  index={p.index}
                  control={p.control}
                  circuitIndex={p.circuitIndex}
                  disabled={p.submitting || p.readonly}
                />
              ) : null}
            </div>
          </div>
          <CreatorApprovedSwapsField
            control={p.control}
            influencerId={p.influencerId}
            fieldPath={
              p.circuitIndex !== undefined
                ? `exercises.${p.circuitIndex}.exercises.${p.index}`
                : `exercises.${p.index}`
            }
            disabled={p.submitting || p.readonly}
          />
        </div>
      </TemplateWrapper>
    </div>
  );
};
export const RestBetween = ({
  index,
  control,
  circuitIndex,
  disabled,
}: {
  index: number;
  control: EditWorkoutVM['control'];
  circuitIndex?: number;
  disabled?: boolean;
}) => (
  <Controller
    name={
      circuitIndex !== undefined
        ? `exercises.${circuitIndex}.exercises.${index}.rest`
        : `exercises.${index}.rest`
    }
    control={control}
    defaultValue={0}
    render={({ field: { onChange, value } }) => (
      <div className="flex flex-col pt-6">
        <InputNumber
          label="Rest between (sec)"
          testId="rest-between"
          value={value ?? undefined}
          onChange={onChange}
          min={0}
          max={300}
          defaultValue={0}
          disabled={disabled}
        />
      </div>
    )}
  />
);

const durationUnits = [
  { label: 'rounds', value: 'rounds' },
  { label: 'sec', value: 'sec' },
  { label: 'min', value: 'min' },
  { label: 'meters', value: 'meters' },
  { label: 'calories', value: 'calories' },
];

export const Duration = ({
  control,
  index,
  submitting,
  circuitIndex,
  readonly,
}: Pick<EditWorkoutVM, 'control' | 'submitting' | 'readonly'> & {
  index: number;
  circuitIndex?: number;
}) => {
  return (
    <Controller
      name={
        circuitIndex !== undefined
          ? `exercises.${circuitIndex}.exercises.${index}.duration`
          : `exercises.${index}.duration`
      }
      control={control}
      render={({ field: { value, onChange }, fieldState }) => {
        return (
          <div className="flex items-end gap-2">
            <InputNumber
              label="Duration"
              disabled={submitting || readonly}
              value={value[0]}
              onChange={(newValue) => onChange([newValue, value[1]])}
              error={fieldState.error?.message}
              min={0}
            />
            <Select
              value={value[1]}
              defaultValue={'min'}
              sx={{
                minWidth: '130px',
                height: 44,
                padding: '8px 16px',
                border: 'solid 2px',
                borderRadius: '10px',
                borderColor: bgColorsVars['N-40'],
                backgroundColor: bgColorsVars['W-100'],
                boxShadow: 'none',
                fontWeight: 500,
                fontFamily: 'inherit',
                margin: '1px',
                '&.Mui-focused': {
                  margnin: 0,
                  boxShadow: 'none',
                  borderWidth: '3px',
                },
              }}
              disabled={readonly}
            >
              {durationUnits.map((option) => (
                <MenuItem
                  key={option.label}
                  value={option.value}
                  onClick={() => onChange([value[0], option.value])}
                >
                  <Typography variant="body" size="large">
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }}
    />
  );
};

const Sets = ({
  sets,
  index,
  dispatch,
  submitting,
  circuitIndex,
  readonly,
  control,
}: Pick<EditWorkoutVM, 'dispatch' | 'submitting' | 'readonly' | 'control'> & {
  index: number;
  circuitIndex?: number;
  sets: Set[];
}) => (
  <div className="flex flex-col gap-3" data-testid="sets" key={index}>
    {sets.map((set: Set, i: number) => (
      <Controller
        name={
          circuitIndex !== undefined
            ? `exercises.${circuitIndex}.exercises.${index}.sets.${i}`
            : `exercises.${index}.sets.${i}`
        }
        key={i}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <SetDoseCard
            label={`Set ${i + 1}`}
            set={(value ?? set) as Set}
            onChange={onChange}
            onUnitChange={(unit) =>
              dispatch('setSetsUnit', { circuitIndex, exercise: index, unit })
            }
            onRemove={
              i === 0
                ? undefined
                : () =>
                    circuitIndex !== undefined && circuitIndex >= 0
                      ? dispatch('removeCircuitSet', {
                          circuitIndex,
                          exercise: index,
                          set: i,
                        })
                      : dispatch('removeSet', { exercise: index, set: i })
            }
            errors={error as SetDoseErrors | undefined}
            disabled={submitting || readonly}
            readonly={readonly}
          />
        )}
      />
    ))}
    <Button
      color="primaryTonal"
      leftIcon={faPlus}
      className="w-[83px]"
      onClick={() =>
        circuitIndex !== undefined && circuitIndex >= 0
          ? dispatch('addCircuitSet', { circuitIndex, index })
          : dispatch('addSet', index)
      }
      disabled={readonly}
    >
      Set
    </Button>
  </div>
);
